<template>
  <div class="tags-view">
    <div class="tags-view-scroll">
      <div
        v-for="tag in visitedViews"
        :key="tag.path"
        class="tags-view-item"
        :class="{ active: isActive(tag) }"
        @click="goTo(tag)"
      >
        <span class="tags-view-title">{{ tag.title }}</span>
        <i
          v-if="!tag.affix"
          class="el-icon-close tags-view-close"
          @click.stop="closeTag(tag)"
        />
      </div>
    </div>
    <el-dropdown class="tags-view-actions" trigger="click" @command="handleCommand">
      <span class="tags-view-more">
        <i class="el-icon-arrow-down" />
      </span>
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item command="closeOthers">关闭其他</el-dropdown-item>
        <el-dropdown-item command="closeAll">全部关闭</el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
  </div>
</template>

<script>
import { mapState } from 'vuex'

// 标签数量上限（含固定的仪表盘标签）
const MAX_TAGS = 20

export default {
  name: 'TagsView',
  data() {
    return {
      // 上一个有效路由，用于超限时回退
      lastPath: ''
    }
  },
  computed: {
    ...mapState('tagsView', ['visitedViews'])
  },
  watch: {
    $route: {
      immediate: true,
      handler(route) {
        if (!route || !route.path || route.path.indexOf('/admin') !== 0) return
        const exists = this.visitedViews.some(v => v.path === route.path)
        // 打开新标签前检查上限，超出则提示并回退
        if (!exists && this.visitedViews.length >= MAX_TAGS) {
          this.$message.warning(`打开的选项卡已达上限（${MAX_TAGS}个），请先关闭之前打开的选项卡`)
          const fallback = this.lastPath || (this.visitedViews[0] && this.visitedViews[0].path)
          if (fallback && fallback !== route.path) {
            this.$router.replace(fallback).catch(() => {})
          }
          return
        }
        this.lastPath = route.path
        this.$store.commit('tagsView/ADD_VIEW', {
          path: route.path,
          title: (route.meta && route.meta.title) || '未命名',
          componentName: (route.meta && route.meta.componentName) || route.name,
          affix: route.path === '/admin/dashboard'
        })
        this.scrollToCurrentTag()
      }
    }
  },
  methods: {
    isActive(tag) {
      return tag.path === this.$route.path
    },
    goTo(tag) {
      if (tag.path !== this.$route.path) {
        this.$router.push(tag.path).catch(() => {})
      }
    },
    closeTag(tag) {
      const wasActive = this.isActive(tag)
      const idx = this.visitedViews.indexOf(tag)
      this.$store.commit('tagsView/DEL_VIEW', tag.path)
      if (wasActive) {
        // 关闭当前页时跳到相邻标签（优先左侧）
        const views = this.visitedViews
        const next = views[idx - 1] || views[idx] || views[0]
        if (next && next.path !== this.$route.path) {
          this.$router.push(next.path).catch(() => {})
        }
      }
    },
    handleCommand(cmd) {
      if (cmd === 'closeOthers') {
        this.$store.commit('tagsView/DEL_OTHERS', this.$route.path)
      } else if (cmd === 'closeAll') {
        this.$store.commit('tagsView/DEL_ALL')
        const first = this.visitedViews[0]
        if (first && first.path !== this.$route.path) {
          this.$router.push(first.path).catch(() => {})
        }
      }
    },
    scrollToCurrentTag() {
      this.$nextTick(() => {
        const items = this.$el.querySelectorAll('.tags-view-item')
        const idx = this.visitedViews.findIndex(v => v.path === this.$route.path)
        if (items[idx] && items[idx].scrollIntoView) {
          items[idx].scrollIntoView({ block: 'nearest', inline: 'nearest' })
        }
      })
    }
  }
}
</script>

<style scoped>
/* 标签栏：胶囊标签风格，白底横条，激活项为蓝色药丸 */
.tags-view {
  display: flex;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  padding: 0 10px;
  height: 38px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.tags-view-scroll {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.tags-view-scroll::-webkit-scrollbar {
  display: none;
}

.tags-view-item {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 12px;
  margin-right: 6px;
  border-radius: 12px;
  background: #f5f7fa;
  color: #606266;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition: color 0.2s, background-color 0.2s, box-shadow 0.2s;
}

.tags-view-item:hover {
  color: #409eff;
  background: #ecf5ff;
}

.tags-view-item.active {
  background: #409eff;
  color: #fff;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.35);
}

.tags-view-item.active:hover {
  background: #409eff;
  color: #fff;
}

.tags-view-title {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

.tags-view-close {
  margin-left: 6px;
  font-size: 12px;
  width: 14px;
  height: 14px;
  line-height: 14px;
  text-align: center;
  border-radius: 50%;
  color: #c0c4cc;
  transition: all 0.2s;
}

.tags-view-item.active .tags-view-close {
  color: rgba(255, 255, 255, 0.85);
}

.tags-view-close:hover {
  background: rgba(0, 0, 0, 0.15);
  color: #fff;
}

.tags-view-item.active .tags-view-close:hover {
  background: rgba(255, 255, 255, 0.3);
  color: #fff;
}

.tags-view-actions {
  margin-left: 6px;
  flex-shrink: 0;
}

.tags-view-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  color: #909399;
  cursor: pointer;
  font-size: 12px;
  background: #f5f7fa;
  transition: color 0.2s;
}

.tags-view-more:hover {
  color: #409eff;
  background: #ecf5ff;
}
</style>
